/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getInsuranceClaims } from 'app/entities/insurance-claim/insurance-claim.reducer';
import { InsuranceClaimItemStatus } from 'app/shared/model/enumerations/insurance-claim-item-status.model';
import { createEntity, getEntity, reset, updateEntity } from './insurance-claim-item.reducer';

export const InsuranceClaimItemUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const insuranceClaims = useAppSelector(state => state.insuranceClaim.entities);
  const insuranceClaimItemEntity = useAppSelector(state => state.insuranceClaimItem.entity);
  const loading = useAppSelector(state => state.insuranceClaimItem.loading);
  const updating = useAppSelector(state => state.insuranceClaimItem.updating);
  const updateSuccess = useAppSelector(state => state.insuranceClaimItem.updateSuccess);
  const insuranceClaimItemStatusValues = Object.keys(InsuranceClaimItemStatus);

  const handleClose = () => {
    navigate(`/insurance-claim-item${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getInsuranceClaims({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.quantityProvided !== undefined && typeof values.quantityProvided !== 'number') {
      values.quantityProvided = Number(values.quantityProvided);
    }
    if (values.quantityApproved !== undefined && typeof values.quantityApproved !== 'number') {
      values.quantityApproved = Number(values.quantityApproved);
    }
    if (values.priceApproved !== undefined && typeof values.priceApproved !== 'number') {
      values.priceApproved = Number(values.priceApproved);
    }
    values.microSec = convertDateTimeToServer(values.microSec);
    values.timestampEntry = convertDateTimeToServer(values.timestampEntry);
    values.lastUpdated = convertDateTimeToServer(values.lastUpdated);
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }

    const entity = {
      ...insuranceClaimItemEntity,
      ...values,
      claim: insuranceClaims.find(it => it.id.toString() === values.claim?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          microSec: displayDefaultDateTime(),
          timestampEntry: displayDefaultDateTime(),
          lastUpdated: displayDefaultDateTime(),
        }
      : {
          status: 'PASSED',
          ...insuranceClaimItemEntity,
          microSec: convertDateTimeFromServer(insuranceClaimItemEntity.microSec),
          timestampEntry: convertDateTimeFromServer(insuranceClaimItemEntity.timestampEntry),
          lastUpdated: convertDateTimeFromServer(insuranceClaimItemEntity.lastUpdated),
          claim: insuranceClaimItemEntity?.claim?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.insuranceClaimItem.home.createOrEditLabel" data-cy="InsuranceClaimItemCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.insuranceClaimItem.home.createOrEditLabel">
              Create or edit a InsuranceClaimItem
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="insurance-claim-item-id"
                  label={translate('tinyMedicRestApp.insuranceClaimItem.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaimItem.quantityProvided')}
                id="insurance-claim-item-quantityProvided"
                name="quantityProvided"
                data-cy="quantityProvided"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaimItem.quantityApproved')}
                id="insurance-claim-item-quantityApproved"
                name="quantityApproved"
                data-cy="quantityApproved"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaimItem.priceApproved')}
                id="insurance-claim-item-priceApproved"
                name="priceApproved"
                data-cy="priceApproved"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaimItem.explanation')}
                id="insurance-claim-item-explanation"
                name="explanation"
                data-cy="explanation"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaimItem.justification')}
                id="insurance-claim-item-justification"
                name="justification"
                data-cy="justification"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaimItem.rejectionReason')}
                id="insurance-claim-item-rejectionReason"
                name="rejectionReason"
                data-cy="rejectionReason"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaimItem.status')}
                id="insurance-claim-item-status"
                name="status"
                data-cy="status"
                type="select"
              >
                {insuranceClaimItemStatusValues.map(insuranceClaimItemStatus => (
                  <option value={insuranceClaimItemStatus} key={insuranceClaimItemStatus}>
                    {translate(`tinyMedicRestApp.InsuranceClaimItemStatus.${insuranceClaimItemStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaimItem.active')}
                id="insurance-claim-item-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaimItem.microSec')}
                id="insurance-claim-item-microSec"
                name="microSec"
                data-cy="microSec"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaimItem.timestampEntry')}
                id="insurance-claim-item-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaimItem.lastUpdated')}
                id="insurance-claim-item-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaimItem.cursor')}
                id="insurance-claim-item-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                id="insurance-claim-item-claim"
                name="claim"
                data-cy="claim"
                label={translate('tinyMedicRestApp.insuranceClaimItem.claim')}
                type="select"
              >
                <option value="" key="0" />
                {insuranceClaims
                  ? insuranceClaims.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/insurance-claim-item" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default InsuranceClaimItemUpdate;
